import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Filter } from "bad-words";
import api from "../Utility/api";
import { useAuth } from "../Utility/AuthContext";
import { blankOption, validateStory } from "../Utility/storyFormat";
import {
  EXAMPLE_ROWS,
  exportWorkbook,
  importWorkbook,
} from "../Utility/storyWorkbook";
import StoryPreview from "../Components/StoryPreview";
import Seo from "../Components/Seo";

const API = process.env.REACT_APP_API_URL || "";
const TAG_FIELDS = [
  ["requirements", "Required tags", "Visible with ANY of these tags."],
  ["blocker", "Blocking tags", "Hidden with ANY of these tags."],
  ["rewards", "Grant tags", "Use restart to clear the reader’s inventory."],
  ["removes", "Remove tags", "Remove these tags after this choice."],
];
export default function CreateStory() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rows, setRows] = useState([blankOption()]);
  const [scene, setScene] = useState(1);
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));
  const [canEdit, setCanEdit] = useState(!id);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const fileInput = useRef();
  useEffect(() => {
    let active = true;
    setCanEdit(!id);
    setLoading(Boolean(id));
    setDirty(false);
    setError("");
    setNotice("");
    setTab(0);
    setScene(1);
    if (!id) {
      setName("");
      setDescription("");
      setRows([blankOption()]);
      return;
    }
    if (!user) return;
    api
      .get(`${API}/api/stories/${id}`)
      .then(({ data }) => {
        if (!active) return;
        if (data.created_by !== user.id)
          throw new Error("Only the creator can edit this story.");
        const parsed = JSON.parse(data.story_text);
        setRows(parsed);
        setName(data.story_name);
        setDescription(data.story_desc || "");
        setCanEdit(true);
      })
      .catch((err) => {
        if (active)
          setError(
            err.response?.data?.error || err.message || "Could not load story.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, user]);
  useEffect(() => {
    const warn = (event) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    const warnOnLink = (event) => {
      if (!dirty) return;
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.download || anchor.target === "_blank") return;
      const target = new URL(anchor.href, window.location.href);
      if (
        target.pathname === window.location.pathname &&
        target.origin === window.location.origin
      )
        return;
      if (
        !window.confirm(
          "Leave the editor with unsaved changes? Download your draft or save it first to keep your work.",
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", warn);
    document.addEventListener("click", warnOnLink, true);
    return () => {
      window.removeEventListener("beforeunload", warn);
      document.removeEventListener("click", warnOnLink, true);
    };
  }, [dirty]);
  const change = useCallback(() => {
    setDirty(true);
    setNotice("");
    setError("");
  }, []);
  const scenes = [...new Set(rows.map((row) => Number(row.key)))].sort(
    (a, b) => a - b,
  );
  const updateRow = (index, field, value) => {
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
    change();
  };
  const addScene = () => {
    const key = scenes.length ? Math.max(...scenes) + 1 : 1;
    setRows((current) => [...current, blankOption(key)]);
    setScene(key);
    change();
  };
  const importFile = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (
      dirty &&
      !window.confirm(
        "Replace all choices in this draft with the workbook? Unsaved choice changes will be lost.",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      const imported = await importWorkbook(file);
      setRows(imported);
      setScene(Number(imported[0].key));
      setTab(0);
      setDirty(true);
      setNotice(
        `Imported ${imported.length} choices from ${file.name}. Review them below, then ${id ? "save changes to replace the story" : "publish your story"}.`,
      );
    } catch (err) {
      setError(err.message || "Unable to read this workbook.");
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setError("");
    setNotice("");
    try {
      if (!name.trim())
        throw new Error("Give your story a title before saving.");
      const content = validateStory(rows);
      const filter = new Filter();
      filter.removeWords(
        "bloody",
        "shit",
        "vomit",
        "sucks",
        "suck",
        "piss",
        "crap",
        "butt",
        "ass",
        "babe",
        "twat",
        "uncut",
      );
      if (
        [
          name,
          description,
          ...content.flatMap((row) => [row.optionText, row.desc]),
        ].some((text) => text && filter.isProfane(text))
      )
        throw new Error(
          "Please remove inappropriate content from the title, description, or choices.",
        );
      setBusy(true);
      const payload = {
        story_name: name,
        story_description: description,
        story_text: JSON.stringify(content),
      };
      const result = id
        ? await api.put(`${API}/api/stories/${id}`, payload)
        : await api.post(`${API}/api/stories/create`, payload);
      setDirty(false);
      if (!id) navigate(`/edit/${result.data.id}`, { replace: true });
      else
        setNotice(
          "Your story has been saved. Readers can play the updated version now.",
        );
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Unable to save your story.",
      );
    } finally {
      setBusy(false);
    }
  };
  if (loading)
    return (
      <Box textAlign="center" p={8}>
        <CircularProgress aria-label="Loading story" />
      </Box>
    );
  if (!canEdit)
    return (
      <Container sx={{ mt: 5 }}>
        <Seo title="Edit story" noindex />
        <Alert severity="error">{error || "You cannot edit this story."}</Alert>
      </Container>
    );
  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 } }}>
      <Seo title={id ? `Edit ${name || "story"}` : "Write a story"} noindex />
      <span className="eyebrow">The writing room</span>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h2" component="h1">
            {id ? "Your next revision." : "A new world awaits."}
          </Typography>
          <Typography color="text.secondary" mt={1}>
            Build each branch, give it a voice, and see where it leads.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Chip
            size="small"
            variant="outlined"
            label={dirty ? "Unsaved changes" : id ? "Saved story" : "New story"}
          />
          <Button variant="contained" onClick={save} disabled={busy}>
            {busy ? "Working…" : id ? "Save changes" : "Publish story"}
          </Button>
        </Stack>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <div className="editor-grid">
          <TextField
            label="Story title"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              change();
            }}
            inputProps={{ maxLength: 200 }}
            fullWidth
            required
          />
          <TextField
            label="Library description"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              change();
            }}
            inputProps={{ maxLength: 2000 }}
            multiline
            minRows={1}
            fullWidth
          />
        </div>
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInput.current.click()}
            disabled={busy}
          >
            {id ? "Upload replacement XLSX" : "Import XLSX"}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx"
            hidden
            onChange={importFile}
            aria-label="Story workbook"
          />
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => exportWorkbook(rows, name || "story")}
            disabled={busy || !rows.length}
          >
            Download current XLSX
          </Button>
          <Button
            onClick={() => exportWorkbook(EXAMPLE_ROWS, "story_template")}
          >
            Get example template
          </Button>
          {id && (
            <Button component={Link} to={`/story/${id}`}>
              Read published story ↗
            </Button>
          )}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1}
        >
          Import replaces choices in this draft; saving updates the published
          story. Downloads include all eight columns and instructions. Saving an
          edit clears old reader bookmarks.
        </Typography>
      </Paper>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ mb: 3 }}
        aria-label="Editor view"
      >
        <Tab label="Write & branch" />
        <Tab label="Play preview" />
      </Tabs>
      {tab === 1 ? (
        <StoryPreview key={id || "new"} rows={rows} />
      ) : (
        <div className="editor-layout">
          <Paper className="scene-sidebar" variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline">Your scenes</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={2}
            >
              {scenes.length} scenes · {rows.length} choices
            </Typography>
            <Stack gap={1}>
              {scenes.map((key) => (
                <Button
                  key={key}
                  variant={scene === key ? "contained" : "text"}
                  onClick={() => setScene(key)}
                  sx={{ justifyContent: "space-between" }}
                >
                  <span>
                    Scene {key}
                    {key === 1 ? " · Start" : ""}
                  </span>
                  <span>
                    {rows.filter((row) => Number(row.key) === key).length}
                  </span>
                </Button>
              ))}
            </Stack>
            <Button
              fullWidth
              startIcon={<AddIcon />}
              onClick={addScene}
              sx={{ mt: 2 }}
            >
              Add scene
            </Button>
          </Paper>
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              gap={1}
              alignItems="center"
              mb={2}
            >
              <Typography variant="h5" component="h2">
                Scene {scene}
              </Typography>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={() => {
                  setRows((current) => [...current, blankOption(scene)]);
                  change();
                }}
              >
                Add choice
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Choices in this scene appear together. After a reader picks one,
              its narrative is shown and the next scene opens. Scene 1 is the
              beginning; destination 0 is an ending.
            </Typography>
            <Stack gap={3}>
              {rows.map((row, index) =>
                Number(row.key) !== scene ? null : (
                  <Card key={index} sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="overline">
                        Choice {index + 1}
                      </Typography>
                      <Stack direction="row">
                        <Button
                          size="small"
                          onClick={() => {
                            setRows((current) => [
                              ...current.slice(0, index + 1),
                              { ...row },
                              ...current.slice(index + 1),
                            ]);
                            change();
                          }}
                        >
                          Duplicate
                        </Button>
                        <Button
                          color="error"
                          size="small"
                          aria-label={`Delete choice ${index + 1}`}
                          onClick={() => {
                            setRows((current) =>
                              current.filter((_, i) => i !== index),
                            );
                            change();
                          }}
                        >
                          <DeleteOutlineIcon />
                        </Button>
                      </Stack>
                    </Stack>
                    <Stack gap={2.5}>
                      <TextField
                        label="Choice button text"
                        value={row.optionText || ""}
                        onChange={(event) =>
                          updateRow(index, "optionText", event.target.value)
                        }
                        fullWidth
                        required
                      />
                      <TextField
                        label="Narrative after this choice"
                        value={row.desc || ""}
                        onChange={(event) =>
                          updateRow(index, "desc", event.target.value)
                        }
                        multiline
                        minRows={4}
                        fullWidth
                      />
                      <div className="editor-grid">
                        <TextField
                          select
                          label="Leads to"
                          value={Number(row.leadsTo)}
                          onChange={(event) =>
                            updateRow(
                              index,
                              "leadsTo",
                              Number(event.target.value),
                            )
                          }
                          fullWidth
                        >
                          <MenuItem value={0}>The end (0)</MenuItem>
                          {[...new Set([...scenes, Number(row.leadsTo)])]
                            .filter((key) => key > 0)
                            .map((key) => (
                              <MenuItem key={key} value={key}>
                                Scene {key}
                                {!scenes.includes(key) ? " (missing)" : ""}
                              </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                          type="number"
                          label="Move to scene (key)"
                          value={row.key}
                          inputProps={{ min: 1, step: 1 }}
                          onChange={(event) => {
                            const key = Number(event.target.value);
                            if (Number.isSafeInteger(key) && key > 0) {
                              updateRow(index, "key", key);
                              setScene(key);
                            }
                          }}
                          helperText="Use a new number to create a scene."
                          fullWidth
                        />
                      </div>
                      <Box component="details">
                        <Typography
                          component="summary"
                          sx={{
                            cursor: "pointer",
                            fontWeight: 600,
                            color: "primary.main",
                          }}
                        >
                          Inventory & conditions
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          my={2}
                        >
                          Separate tags with commas or |. Tag names are case
                          sensitive. Required tags use the existing ANY-tag
                          rule.
                        </Typography>
                        <div className="editor-grid">
                          {TAG_FIELDS.map(([field, label, help]) => (
                            <TextField
                              key={field}
                              label={label}
                              helperText={help}
                              value={row[field] || ""}
                              onChange={(event) =>
                                updateRow(index, field, event.target.value)
                              }
                              fullWidth
                            />
                          ))}
                        </div>
                      </Box>
                    </Stack>
                  </Card>
                ),
              )}
            </Stack>
            {!rows.some((row) => Number(row.key) === scene) && (
              <Alert severity="info">
                This scene has no choices. Add one above, or select another
                scene.
              </Alert>
            )}
          </Box>
        </div>
      )}
    </Container>
  );
}
